/**
 * Homepage "Need a Custom Order?" request modal.
 *
 * An independent flow from assets/bpf-homepage-pet-request.js's existing
 * "Don't see your pet?" modal — separate markup (data-custom-order-*
 * attributes throughout, never data-pet-request-*), separate validation,
 * separate Klaviyo event identity, separate success/error state. Neither
 * file reads the other's markup or state; both can be on/off
 * independently (section.settings.enable_pet_request /
 * enable_custom_order_request).
 *
 * Open/close, focus trap, Escape-to-close, background-scroll lock, and
 * focus-return-to-opener are handled by the theme's existing
 * <modal-dialog>/<modal-opener> custom elements (assets/global.js),
 * exactly like the pet-request modal — this file only owns: conditional
 * nothing (every field here is independently optional/required, no
 * field hides another), validation, photo upload, Klaviyo
 * identify+track, success/error state, and resetting the form when the
 * modal closes.
 *
 * PHOTO UPLOAD: reuses the existing, already-live POST /apps/memorial/image
 * App Proxy route unchanged (the SAME endpoint assets/bpf-picture-frame-
 * product.js and the Memorial Builder's own uploadBuilderImage() already
 * use) — no new backend route, no parallel upload architecture, no
 * storage credentials of any kind in this file. Upload happens eagerly
 * on file selection (same UX as the Picture Frame page), and the
 * resulting Shopify File GID is carried in the submission payload as a
 * reference URL/GID — never the raw image bytes a second time.
 *
 * TRACKING (Part 9): fires a client-side analytics-safe event
 * (`Custom Order Request Submitted`) with ONLY non-sensitive dimensions
 * (pet_type, memorial_style, breed_present, color_present,
 * photo_attached) — the free-text description is deliberately NEVER a
 * property on this event, per the explicit instruction that it belongs
 * in the operational request record, not general analytics.
 *
 * SUPPORT NOTIFICATION (Part 5): this codebase has no existing backend
 * mechanism that sends email directly from the storefront — the
 * existing "Don't see your pet?" flow (the only established BPF
 * customer-request pattern) is ALSO Klaviyo-only, with no email-sending
 * code anywhere in this theme. Following that same proven pattern, this
 * file fires a SECOND, separately-named Klaviyo event,
 * `Custom Order Support Notification`, carrying the full operational
 * payload (pet type, breed, memorial color, memorial style, email, full
 * description, photo reference, timestamp, source, reference id) deliberately kept OUT of
 * the analytics event above. Delivering this to
 * customersupport@bestpetsforever.com still requires a one-time Klaviyo
 * Flow (configured in the Klaviyo dashboard, not theme code) triggered
 * on this event name — the same manual step the existing pet-request
 * flow's own Klaviyo event would already require for any staff
 * follow-up, since no code in this theme has ever sent email directly.
 * See this feature's final report for this limitation stated plainly.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var MAX_DESCRIPTION_LENGTH = 500;
  var MAX_PHOTO_BYTES = 10 * 1024 * 1024;
  var ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  var UPLOAD_TIMEOUT_MS = 30000;

  function trim(value) {
    return (value || '').replace(/^\s+|\s+$/g, '');
  }

  function removeStaleModalDuplicates(id) {
    if (!id || !window.CSS || typeof window.CSS.escape !== 'function') return;
    var matches = document.querySelectorAll('#' + window.CSS.escape(id));
    for (var i = 0; i < matches.length - 1; i++) {
      if (matches[i].parentNode) matches[i].parentNode.removeChild(matches[i]);
    }
  }

  function generateReferenceId() {
    var rand = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID().replace(/-/g, '') : String(Date.now()) + Math.random().toString(36).slice(2);
    return 'cor_' + rand;
  }

  function generateSubmissionId() {
    var rand = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID().replace(/-/g, '') : String(Date.now()) + Math.random().toString(36).slice(2);
    return 'cor_' + rand;
  }

  function initCustomOrderForm(form) {
    if (form.hasAttribute('data-custom-order-initialized')) return;
    var dialogRoot = form.closest('.bpf-pet-request-modal__dialog');
    var modal = form.closest('modal-dialog');
    if (!dialogRoot || !modal) return;

    form.setAttribute('data-custom-order-initialized', 'true');
    removeStaleModalDuplicates(modal.id);

    var formState = dialogRoot.querySelector('[data-custom-order-form-state]');
    var successState = dialogRoot.querySelector('[data-custom-order-success-state]');
    var petTypeInputs = form.querySelectorAll('[data-custom-order-pet-type]');
    var styleInputs = form.querySelectorAll('[data-custom-order-style]');
    var breedInput = form.querySelector('[data-custom-order-breed-input]');
    var memorialColorInput = form.querySelector('[data-custom-order-memorial-color-input]');
    var emailInput = form.querySelector('[data-custom-order-email-input]');
    var descriptionInput = form.querySelector('[data-custom-order-description-input]');
    var charCount = form.querySelector('[data-custom-order-char-count]');
    var submitButton = form.querySelector('[data-custom-order-submit]');
    var submitLabel = form.querySelector('[data-custom-order-submit-label]');
    var submitError = form.querySelector('[data-custom-order-submit-error]');
    var closeSuccessButton = dialogRoot.querySelector('[data-custom-order-close-success]');

    var uploadBtn = form.querySelector('[data-custom-order-upload-btn]');
    var fileInput = form.querySelector('[data-custom-order-file-input]');
    var photoStatus = form.querySelector('[data-custom-order-photo-status]');
    var photoFilename = form.querySelector('[data-custom-order-filename]');
    var photoState = form.querySelector('[data-custom-order-upload-state]');
    var photoActions = form.querySelector('[data-custom-order-photo-actions]');
    var replaceBtn = form.querySelector('[data-custom-order-replace-btn]');
    var removeBtn = form.querySelector('[data-custom-order-remove-btn]');
    var photoError = form.querySelector('[data-custom-order-photo-error]');

    var defaultSubmitLabel = submitLabel ? submitLabel.textContent : 'SUBMIT CUSTOM REQUEST';
    var isSubmitting = false;
    var photo = { state: 'idle', gid: null, url: null, uploadToken: 0 }; // idle | uploading | uploaded

    function clearFieldError(name) {
      var el = form.querySelector('[data-custom-order-error="' + name + '"]');
      if (el) el.hidden = true;
    }

    function setFieldError(name, input) {
      var el = form.querySelector('[data-custom-order-error="' + name + '"]');
      if (el) el.hidden = false;
      if (input) input.setAttribute('aria-invalid', 'true');
    }

    function clearAllErrors() {
      form.querySelectorAll('[data-custom-order-error]').forEach(function (el) {
        el.hidden = true;
      });
      form.querySelectorAll('[aria-invalid]').forEach(function (el) {
        el.removeAttribute('aria-invalid');
      });
      if (submitError) submitError.hidden = true;
    }

    function currentPetType() {
      var checked = form.querySelector('[data-custom-order-pet-type]:checked');
      return checked ? checked.value : '';
    }

    function currentMemorialStyle() {
      var checked = form.querySelector('[data-custom-order-style]:checked');
      return checked ? checked.value : '';
    }

    // ----- character count -----

    function updateCharCount() {
      if (!charCount || !descriptionInput) return;
      var len = descriptionInput.value.length;
      charCount.textContent = len + ' / ' + MAX_DESCRIPTION_LENGTH;
    }

    if (descriptionInput) {
      descriptionInput.addEventListener('input', function () {
        // maxlength already prevents typing past the limit in every
        // modern browser; this is a defensive hard-trim so a
        // programmatic value set (e.g. paste on an older engine) can
        // never exceed it either.
        if (descriptionInput.value.length > MAX_DESCRIPTION_LENGTH) {
          descriptionInput.value = descriptionInput.value.slice(0, MAX_DESCRIPTION_LENGTH);
        }
        updateCharCount();
        clearFieldError('description');
      });
    }

    petTypeInputs.forEach(function (input) {
      input.addEventListener('change', function () { clearFieldError('pet_type'); });
    });

    // ----- photo upload -----

    function setPhotoIdleUi() {
      if (uploadBtn) uploadBtn.hidden = false;
      if (photoStatus) photoStatus.hidden = true;
      if (photoActions) photoActions.hidden = true;
      if (photoFilename) photoFilename.textContent = '';
      if (photoState) photoState.textContent = '';
    }

    function setPhotoUploadingUi(filename) {
      if (uploadBtn) uploadBtn.hidden = true;
      if (photoStatus) photoStatus.hidden = false;
      if (photoFilename) photoFilename.textContent = filename;
      if (photoState) photoState.textContent = 'Uploading…';
      if (photoActions) photoActions.hidden = true;
    }

    function setPhotoUploadedUi(filename) {
      if (uploadBtn) uploadBtn.hidden = true;
      if (photoStatus) photoStatus.hidden = false;
      if (photoFilename) photoFilename.textContent = filename;
      if (photoState) photoState.textContent = 'Photo added';
      if (photoActions) photoActions.hidden = false;
    }

    function showPhotoError(message) {
      if (!photoError) return;
      photoError.textContent = message;
      photoError.hidden = false;
    }

    function clearPhotoError() {
      if (!photoError) return;
      photoError.textContent = '';
      photoError.hidden = true;
    }

    function messageForUploadStatus(status) {
      switch (status) {
        case 400:
          return 'Please choose a JPEG, PNG, or WebP photo under 10 MB.';
        case 401:
          return 'We couldn’t upload that photo right now. Please try again shortly.';
        case 429:
          return 'We’ve received several uploads from this connection. Please wait a little while and try again.';
        default:
          return 'We couldn’t upload that photo right now. Please try again.';
      }
    }

    function uploadPhoto(file) {
      var myToken = ++photo.uploadToken;
      var hadExistingPhoto = photo.state === 'uploaded';
      var previousGid = photo.gid;
      var previousUrl = photo.url;

      photo.state = 'uploading';
      clearPhotoError();
      setPhotoUploadingUi(file.name);

      var formData = new FormData();
      formData.append('image', file, file.name);
      formData.append('source_context', 'custom_order_request');
      formData.append('submission_id', generateSubmissionId());

      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timeoutId = controller ? setTimeout(function () { controller.abort(); }, UPLOAD_TIMEOUT_MS) : null;

      fetch('/apps/memorial/image', { method: 'POST', body: formData, signal: controller ? controller.signal : undefined })
        .then(function (response) {
          return response.json().catch(function () { return {}; }).then(function (body) {
            return { status: response.status, body: body };
          });
        })
        .then(function (result) {
          if (timeoutId !== null) clearTimeout(timeoutId);
          if (myToken !== photo.uploadToken) return;

          if (result.status !== 201 || !result.body || typeof result.body.gid !== 'string') {
            handleUploadFailure(hadExistingPhoto, previousGid, previousUrl, messageForUploadStatus(result.status));
            return;
          }

          photo.gid = result.body.gid;
          // Shopify's own served URL for this upload, straight from the
          // trusted /apps/memorial/image response — never constructed
          // here. Nullable: older/degraded responses or a non-image file
          // may omit it, in which case the support notification simply
          // omits photo_url too (Part: viewable photo URL).
          photo.url = typeof result.body.url === 'string' ? result.body.url : null;
          photo.state = 'uploaded';
          setPhotoUploadedUi(file.name);
        })
        .catch(function (err) {
          if (timeoutId !== null) clearTimeout(timeoutId);
          if (myToken !== photo.uploadToken) return;
          var message = err && err.name === 'AbortError' ? 'That took too long. Please try again.' : 'We couldn’t upload that photo right now. Please try again.';
          handleUploadFailure(hadExistingPhoto, previousGid, previousUrl, message);
        });
    }

    function handleUploadFailure(hadExistingPhoto, previousGid, previousUrl, message) {
      showPhotoError(message);
      if (hadExistingPhoto) {
        photo.gid = previousGid;
        photo.url = previousUrl;
        photo.state = 'uploaded';
        setPhotoUploadedUi(photoFilename ? photoFilename.textContent : '');
      } else {
        photo.gid = null;
        photo.url = null;
        photo.state = 'idle';
        setPhotoIdleUi();
      }
    }

    function removePhoto() {
      photo.uploadToken++;
      photo.gid = null;
      photo.url = null;
      photo.state = 'idle';
      clearPhotoError();
      setPhotoIdleUi();
    }

    if (uploadBtn) uploadBtn.addEventListener('click', function () { fileInput.click(); });
    if (replaceBtn) replaceBtn.addEventListener('click', function () { fileInput.click(); });
    if (removeBtn) removeBtn.addEventListener('click', removePhoto);
    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        fileInput.value = '';
        if (!file) return;
        clearPhotoError();
        if (ACCEPTED_PHOTO_TYPES.indexOf(file.type) === -1) {
          showPhotoError('Please choose a JPEG, PNG, or WebP photo.');
          return;
        }
        if (file.size > MAX_PHOTO_BYTES) {
          showPhotoError('Please choose a photo under 10 MB.');
          return;
        }
        uploadPhoto(file);
      });
    }

    // ----- reset / success -----

    function resetForm() {
      isSubmitting = false;
      form.reset();
      clearAllErrors();
      updateCharCount();
      removePhoto();
      if (submitButton) submitButton.disabled = false;
      if (submitLabel) submitLabel.textContent = defaultSubmitLabel;
      if (formState) formState.hidden = false;
      if (successState) successState.hidden = true;
    }

    function showSuccess() {
      if (formState) formState.hidden = true;
      if (successState) {
        successState.hidden = false;
        var heading = successState.querySelector('.bpf-pet-request-modal__heading');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus();
        }
      }
    }

    function submissionContext() {
      return window.Shopify && window.Shopify.designMode ? 'theme_editor' : 'storefront';
    }

    function klaviyoQueueAvailable() {
      return !!(window.klaviyo && typeof window.klaviyo.push === 'function');
    }

    // Part 9 — analytics-safe dimensions only. The free-text description
    // is NEVER a property here.
    function submitAnalyticsEvent(payload) {
      window.klaviyo.push([
        'track',
        'Custom Order Request Submitted',
        {
          pet_type: payload.pet_type,
          memorial_style: payload.memorial_style || null,
          breed_present: !!payload.breed,
          color_present: !!payload.memorial_color,
          photo_attached: !!payload.photo_gid,
          submission_context: payload.submission_context,
          reference_id: payload.reference_id
        }
      ]);
    }

    // Part 5 — the operational support record. Carries the full
    // description/photo reference deliberately kept off the analytics
    // event above. A Klaviyo Flow triggered on THIS event name is the
    // mechanism that would actually deliver the
    // customersupport@bestpetsforever.com notification — see this
    // file's own header comment for why.
    //
    // photo_url: Shopify's own served MediaImage URL (from the same
    // trusted /apps/memorial/image upload response as photo_gid, never
    // constructed from the GID) so support staff can click straight to
    // the image. Null when there's no photo, or if Shopify's response
    // didn't include one. photo_gid is kept alongside it for internal use.
    function submitSupportNotification(payload) {
      window.klaviyo.push([
        'track',
        'Custom Order Support Notification',
        {
          pet_type: payload.pet_type,
          breed: payload.breed || null,
          memorial_color: payload.memorial_color || null,
          memorial_style: payload.memorial_style || null,
          email: payload.email,
          description: payload.description,
          photo_gid: payload.photo_gid || null,
          photo_url: payload.photo_url || null,
          submitted_at: payload.submitted_at,
          source: 'Homepage Custom Order Request',
          reference_id: payload.reference_id
        }
      ]);
    }

    function submitToKlaviyo(payload) {
      if (!klaviyoQueueAvailable()) return false;
      try {
        window.klaviyo.push(['identify', { email: payload.email }]);
        submitAnalyticsEvent(payload);
        submitSupportNotification(payload);
        return true;
      } catch (err) {
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn('[bpf-homepage-custom-order] Klaviyo tracking failed', err);
        }
        return false;
      }
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (isSubmitting) return;

      if (photo.state === 'uploading') {
        if (submitError) {
          submitError.textContent = 'Please wait for your photo to finish uploading, or remove it to continue without one.';
          submitError.hidden = false;
        }
        return;
      }

      clearAllErrors();
      if (submitError) submitError.textContent = "We couldn't submit your request. Please try again.";

      var petType = currentPetType();
      var breed = trim(breedInput.value);
      var memorialColor = trim(memorialColorInput.value);
      var memorialStyle = currentMemorialStyle();
      var email = trim(emailInput.value);
      var description = trim(descriptionInput.value);
      var firstInvalid = null;

      if (!petType) {
        setFieldError('pet_type');
        firstInvalid = firstInvalid || petTypeInputs[0];
      }
      if (!email || !EMAIL_RE.test(email)) {
        setFieldError('email', emailInput);
        firstInvalid = firstInvalid || emailInput;
      }
      if (!description) {
        setFieldError('description', descriptionInput);
        firstInvalid = firstInvalid || descriptionInput;
      } else if (description.length > MAX_DESCRIPTION_LENGTH) {
        setFieldError('description', descriptionInput);
        firstInvalid = firstInvalid || descriptionInput;
      }

      if (firstInvalid) {
        firstInvalid.focus();
        return;
      }

      isSubmitting = true;
      if (submitButton) submitButton.disabled = true;
      if (submitLabel) submitLabel.textContent = 'Submitting…';

      var payload = {
        pet_type: petType,
        breed: breed,
        memorial_color: memorialColor,
        memorial_style: memorialStyle,
        email: email,
        description: description,
        photo_gid: photo.state === 'uploaded' ? photo.gid : null,
        photo_url: photo.state === 'uploaded' ? photo.url : null,
        submission_context: submissionContext(),
        submitted_at: new Date().toISOString(),
        reference_id: generateReferenceId()
      };

      var accepted = submitToKlaviyo(payload);

      if (!accepted) {
        isSubmitting = false;
        if (submitButton) submitButton.disabled = false;
        if (submitLabel) submitLabel.textContent = defaultSubmitLabel;
        if (submitError) submitError.hidden = false;
        return;
      }

      showSuccess();
    });

    if (closeSuccessButton) {
      closeSuccessButton.addEventListener('click', function () {
        modal.hide();
      });
    }

    if (window.MutationObserver) {
      var wasOpen = modal.hasAttribute('open');
      var observer = new MutationObserver(function () {
        var isOpen = modal.hasAttribute('open');
        if (wasOpen && !isOpen) resetForm();
        wasOpen = isOpen;
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }

    updateCharCount();
  }

  document.querySelectorAll('[data-custom-order-form]').forEach(initCustomOrderForm);

  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('[data-custom-order-form]').forEach(initCustomOrderForm);
  });
})();
